/**
 * Compute month-precision gaps between consecutive experience or education entries.
 * Each entry can have `start`, `end` (string like "2021-03", "Mar 2021", "2021", or "Present").
 * Returns {labeledItems, gaps}: labeled items are the original list with `_start`, `_end`,
 * `_gapBeforeMonths`, `_gapBeforeLabel`, `_missing` fields appended. Items are returned in
 * the same order (most-recent-first is the convention from AI parser).
 */
const MONTHS = {
    jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3,
    may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7,
    sep: 8, sept: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
};

export function parseDateLike(value) {
    if (!value && value !== 0) return null;
    if (value instanceof Date && !isNaN(value)) return value;
    const s = String(value).trim().toLowerCase();
    if (!s || s === "present" || s === "current" || s === "now" || s === "till date" || s === "to date") return "present";
    // YYYY-MM or YYYY/MM or YYYY.MM
    let m = s.match(/^(\d{4})[-/. ](\d{1,2})$/);
    if (m) return new Date(Number(m[1]), Math.min(11, Math.max(0, Number(m[2]) - 1)), 1);
    // YYYY-MM-DD
    m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    // YYYY only
    m = s.match(/^(\d{4})$/);
    if (m) return new Date(Number(m[1]), 0, 1);
    // "Mar 2021" or "March 2021"
    m = s.match(/^([a-z.]+)\.?\s+(\d{4})$/);
    if (m && MONTHS[m[1].replace(/\./g, "")] !== undefined) {
        return new Date(Number(m[2]), MONTHS[m[1].replace(/\./g, "")], 1);
    }
    // "2021 Mar"
    m = s.match(/^(\d{4})\s+([a-z.]+)$/);
    if (m && MONTHS[m[2].replace(/\./g, "")] !== undefined) {
        return new Date(Number(m[1]), MONTHS[m[2].replace(/\./g, "")], 1);
    }
    return null;
}

function monthsBetween(a, b) {
    // both Date objects; "present" handled outside
    return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}

export function humanMonths(months) {
    if (months === null || months === undefined) return null;
    const n = Math.max(0, Math.round(months));
    if (n < 1) return null;
    if (n < 12) return `${n}m`;
    const y = Math.floor(n / 12);
    const m = n - y * 12;
    if (m === 0) return `${y}y`;
    return `${y}y ${m}m`;
}

/**
 * Items: array of {start, end} (most-recent-first). Returns annotated copy.
 * - `_missing`: boolean, true when start is unparseable AND end is missing/unparseable
 * - `_gapBeforeMonths`: months between this entry's end and the previous (more recent) entry's start
 * - `_gapBeforeLabel`: human label like "8m" / "1y 2m"
 */
export function annotateTimeline(items) {
    if (!Array.isArray(items) || items.length === 0) return [];
    const today = new Date();
    const parsed = items.map((it) => {
        const startRaw = it?.start || it?.start_date || it?.from;
        const endRaw = it?.end || it?.end_date || it?.to;
        const start = parseDateLike(startRaw);
        const end = parseDateLike(endRaw);
        const startMissing = !start && !startRaw;
        const endMissing = !end && !endRaw;
        return {
            ...it,
            _start: start instanceof Date ? start : null,
            _end: end instanceof Date ? end : (end === "present" ? today : null),
            _startMissing: startMissing,
            _endMissing: endMissing,
        };
    });
    // Annotate gap between previous (more recent) and current entry's end
    for (let i = 0; i < parsed.length; i++) {
        if (i === 0) {
            // Top-most entry: check the gap from its end to today (only if end is in the past)
            const it = parsed[i];
            if (it._end && it._end < today && !it._endMissing) {
                const gap = monthsBetween(it._end, today);
                if (gap >= 2) it._gapAfter = { months: gap, label: humanMonths(gap), reason: "since most recent role" };
            }
            continue;
        }
        const prev = parsed[i - 1];
        const cur = parsed[i];
        if (prev._start && cur._end) {
            const gap = monthsBetween(cur._end, prev._start);
            if (gap >= 2) {
                cur._gapBefore = { months: gap, label: humanMonths(gap) };
            }
        }
    }
    return parsed;
}

/** True if at least one obvious field is missing on the item. */
export function hasMissingInfo(item) {
    if (!item || typeof item !== "object") return false;
    const requiredKeys = ["title", "company", "start"];
    return requiredKeys.some((k) => !(item[k] || item[k === "title" ? "role" : k === "company" ? "institution" : ""] || ""));
}
